/**
 * TASKS.EDIT
 * Edit an action plan
 */
import { connect } from "react-redux"
import { ActionPlanState, ActionPlanTask } from "@/redux/action-plan.types"
import { Session } from "@/redux/_session.types"
import TasksDraftWidget from "@/widgets/tasks-draft.widget"
import { useEffect, useState } from "react"
import { STATUS_SAVING } from "@/redux/_status.types"
import { store } from "@/core/root-reducer"
import { actionPlanUpdateAndStatus } from "@/redux/action-plan.actions"
import { FilterState } from "@/redux/filter.types"
import { loadParticipation, loadQuestionsAndTopics } from "@/redux/_archive.actions"
import { WithTranslation, withTranslation } from "react-i18next"
import TasksListWidget from "@/widgets/tasks-list.widget"
import { SurveyState } from "@/redux/survey.types"
import NavDashboard from "@/nav/nav.dashboard"
import { UserState } from "@/redux/user.types"
import NavFocus from "@/nav/nav.focus"
import PageHeader from "@/components/page-header"
import AdmUserPopulationsWidget from "@/widgets/adm-user-populations.widget"
import TasksActionsWidget from "@/widgets/tasks-actions.widget"
import { useNavigate } from "react-router-dom"
import DashboardFiltersWidget from "@/widgets/dashboard-filters.widget"
import TasksEditModal from "@/modals/tasks-edit.modal"
import { topicActivate } from "@/redux/topic.actions"
import { TopicState } from "@/redux/topic.types"
import { getActionPlanAccess } from "@/utils/observer-scope-options.utils"

interface StateProps extends WithTranslation {
  _session: Session
  actionPlan: ActionPlanState
  filter: FilterState
  survey: SurveyState
  topic: TopicState
  user: UserState
}

function TasksEditRoute(props: StateProps) {
  const navigate = useNavigate()
  const { t } = props
  const [currentTask, setCurrentTask] = useState<ActionPlanTask | null>(null)

  //Load data
  useEffect(() => {
    store.dispatch(loadQuestionsAndTopics(props.filter.dashboard))
    store.dispatch(loadParticipation(props.filter.dashboard))
  }, [props.filter.dashboard])

  //Detect changes in order to save
  useEffect(() => {
    if (props.actionPlan.status === STATUS_SAVING) {
      update()
    }

    async function update() {
      store.dispatch(actionPlanUpdateAndStatus())
    }
  }, [props.actionPlan.status])

  //Load modal if specified in the query
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const taskId = searchParams.get("taskId")
    const task = props.actionPlan.active.tasks.find((x) => x.id === taskId)
    if (task && props.topic.list.length > 0 && currentTask?.id !== task.id) {
      const activeTask = new ActionPlanTask(task)
      store.dispatch(topicActivate(activeTask.getTopic(props.topic.list)))
      setCurrentTask(activeTask)
    }
  }, [])

  function closeTaskModal() {
    setCurrentTask(null)
    navigate("/tasks/edit", { replace: true })
  }

  //Render specific widget according status
  function TaskWidget() {
    switch (props.actionPlan.active.status) {
      case "draft":
        return <TasksDraftWidget />
      default:
        return <TasksListWidget />
    }
  }

  //Display dashboard for other manager
  if (
    props.user.active.id !== "" &&
    props.user.active.id !== props._session.userId &&
    (props._session.userRole === "ADMIN" || getActionPlanAccess(props._session) === "scopeWrite")
  ) {
    return (
      <NavFocus status={props.actionPlan.status} customActions={<TasksActionsWidget />}>
        <PageHeader
          title={t("module_task_uniq") + " " + props.user.active.username}
          subtitle={t("survey") + " " + props.survey.active.name}>
          <AdmUserPopulationsWidget currentUser={props.actionPlan.active.User} />
        </PageHeader>
        {props.actionPlan.active.status === "draft" && (
          <DashboardFiltersWidget hideOverflow currentFilters={props.filter.list} currentSurvey={props.survey.active} />
        )}
        {currentTask && <TasksEditModal currentTask={currentTask} isModeActive onClose={closeTaskModal} />}
        {TaskWidget()}
      </NavFocus>
    )
  }
  //Display action plan for myself
  else {
    return (
      <NavDashboard
        title={t("tasks_title_" + props.actionPlan.active.status)}
        status={props.actionPlan.status}
        helpKey="dashboard.actions">
        {currentTask && <TasksEditModal currentTask={currentTask} onClose={closeTaskModal} />}
        {TaskWidget()}
      </NavDashboard>
    )
  }
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan,
  filter: state.filter,
  survey: state.survey,
  topic: state.topic,
  user: state.user
})

export default withTranslation()(connect(mapStateToProps)(TasksEditRoute))
